import { useEffect, useState, useCallback } from 'react';
import { pdfjs } from 'react-pdf';
import {
  Table,
  Tag,
  Typography,
  Button,
  Segmented,
  Spin,
  Empty,
  Space,
  Tooltip,
  Row,
  Col,
  Card,
  Statistic,
  Divider,
  Input,
} from 'antd';
import {
  ExperimentOutlined,
  LoadingOutlined,
  ArrowRightOutlined,
  ArrowLeftOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  PdfViewer,
  pdfMapperToNormalized,
  normalizedToPixels,
} from '@ute/mapper-ui-components';
import type { PdfPageGeometry } from '@ute/mapper-ui-components';
import {
  mappingApi,
  type LabRecord,
  type LabRecordDetail,
} from '../services/api/mapping';

const { Title, Text } = Typography;

type Side = 'before' | 'after';

function decisionColor(decision: string | null) {
  if (decision === 'RECONCILED') return 'green';
  if (decision === 'NEW_TEMPLATE') return 'blue';
  if (decision === 'KEEP_EXISTING') return 'orange';
  return 'default';
}

function pct(n: number | null | undefined) {
  if (n == null) return '—';
  return `${Math.round(n * 100)}%`;
}

/** Pull extracted field values out of the S3 extraction blob.
 *  The blob is either { [mappingKey]: { data: [...] } } or { data: [...] }. */
function extractDocData(blob: any, mappingKey: string): Record<string, any> {
  if (!blob) return {};
  const byKey = blob[mappingKey];
  const dataArr = byKey?.data ?? blob?.data;
  const first = Array.isArray(dataArr) ? dataArr[0] : dataArr;
  return first && typeof first === 'object' ? first : {};
}

interface CoordBox {
  fieldName: string; // semantic key, e.g. "orderNumber"
  coordKey: string; // config key, e.g. "orderNumberCoords"
  label: string;
  normalized: { x: number; y: number; w: number; h: number };
  page: number;
}

type PatternBoxMap = Record<
  string,
  { x: number; y: number; w: number; h: number; page: number } | null
>;

/** Extract all changed *Coords keys from appliedPatch and convert them to normalised boxes. */
function buildChangedCoordBoxes(
  appliedPatch: Record<string, { from: unknown; to: unknown }> | null,
  beforeConfig: Record<string, any>,
  afterConfig: Record<string, any>,
  geometry: PdfPageGeometry
): { before: CoordBox[]; after: CoordBox[] } {
  if (!appliedPatch || !geometry.pageWidth) return { before: [], after: [] };

  const changedCoordKeys = Object.keys(appliedPatch).filter((k) =>
    k.endsWith('Coords')
  );
  const toCoordBox = (
    coordKey: string,
    config: Record<string, any>
  ): CoordBox | null => {
    const coords = config[coordKey];
    if (!coords) return null;
    const normalized = pdfMapperToNormalized(
      coords,
      geometry.pageWidth,
      geometry.pageHeight
    );
    if (!normalized) return null;
    const fieldName = coordKey.replace(/Coords$/, '');
    const pageNo = config[`${fieldName}PageNo`];
    return {
      fieldName,
      coordKey,
      label: fieldName,
      normalized,
      page: pageNo ? parseInt(pageNo, 10) : 1,
    };
  };

  return {
    before: changedCoordKeys
      .map((k) => toCoordBox(k, beforeConfig))
      .filter(Boolean) as CoordBox[],
    after: changedCoordKeys
      .map((k) => toCoordBox(k, afterConfig))
      .filter(Boolean) as CoordBox[],
  };
}

import {
  type TextItem,
  groupItemsIntoLines,
  buildRegex,
  calcPatternBox,
  calcCaptureGroupBox,
} from '../utils/patternHighlight';

async function resolvePatternBoxes(
  pdfUrl: string,
  changedPatternKeys: string[],
  beforeConfig: Record<string, any>,
  afterConfig: Record<string, any>,
  pdfWidth: number,
  pdfHeight: number
): Promise<{ before: PatternBoxMap; after: PatternBoxMap }> {
  const before: PatternBoxMap = {};
  const after: PatternBoxMap = {};
  for (const k of changedPatternKeys) {
    before[k] = null;
    after[k] = null;
  }

  try {
    const doc = await pdfjs.getDocument(pdfUrl).promise;

    for (let pageNum = 1; pageNum <= doc.numPages; pageNum++) {
      // Stop early if all patterns are resolved on both sides
      const allResolved = changedPatternKeys.every(
        (k) => before[k] !== null && after[k] !== null
      );
      if (allResolved) break;

      const page = await doc.getPage(pageNum);
      const viewport = page.getViewport({ scale: 1 });
      const pageHeight = viewport.height;
      const content = await page.getTextContent();
      const lines = groupItemsIntoLines(content.items as TextItem[]);

      console.log(`[patternHighlight] page ${pageNum}: ${lines.length} lines`);
      lines.forEach((l) => console.log(`  [${l.str}]`));

      for (const patternKey of changedPatternKeys) {
        const beforePattern: string | undefined = beforeConfig[patternKey];
        const afterPattern: string | undefined = afterConfig[patternKey];
        // Boundary patterns (header/footer) use full-string match (Java String.matches).
        // Field patterns use partial find (Groovy =~ operator).
        const isBoundary =
          patternKey === 'lineItemsHeaderPattern' ||
          patternKey === 'lineItemsFooterPattern';

        for (const line of lines) {
          if (beforePattern && before[patternKey] === null) {
            const bm = buildRegex(beforePattern, isBoundary).exec(line.str);
            if (bm) {
              const captured = !isBoundary && bm[1] != null ? bm[1] : null;
              before[patternKey] = captured
                ? calcCaptureGroupBox(
                    line,
                    captured,
                    pageHeight,
                    pdfWidth,
                    pdfHeight
                  ) ?? calcPatternBox(line, pageHeight, pdfWidth, pdfHeight)
                : calcPatternBox(line, pageHeight, pdfWidth, pdfHeight);
              before[patternKey]!.page = pageNum;
              console.log(
                `[patternHighlight] ${patternKey} before p${pageNum}: [${line.str}] capture=[${captured}]`
              );
            }
          }
          if (afterPattern && after[patternKey] === null) {
            const am = buildRegex(afterPattern, isBoundary).exec(line.str);
            if (am) {
              const captured = !isBoundary && am[1] != null ? am[1] : null;
              after[patternKey] = captured
                ? calcCaptureGroupBox(
                    line,
                    captured,
                    pageHeight,
                    pdfWidth,
                    pdfHeight
                  ) ?? calcPatternBox(line, pageHeight, pdfWidth, pdfHeight)
                : calcPatternBox(line, pageHeight, pdfWidth, pdfHeight);
              after[patternKey]!.page = pageNum;
              console.log(
                `[patternHighlight] ${patternKey} after p${pageNum}: [${line.str}] capture=[${captured}]`
              );
            }
          }
        }
      }
    }
  } catch (error) {
    console.error('[patternHighlight] failed:', error);
  }

  return { before, after };
}

function patternMapToBoxes(map: PatternBoxMap): CoordBox[] {
  return Object.entries(map)
    .filter(([, box]) => box != null)
    .map(([patternKey, box]) => ({
      fieldName: patternKey.replace(/Pattern$/, ''),
      coordKey: patternKey,
      label: `${patternKey.replace(/Pattern$/, '')} (pattern)`,
      normalized: { x: box!.x, y: box!.y, w: box!.w, h: box!.h },
      page: box!.page,
    }));
}

interface ExtractionRow {
  field: string;
  before: string;
  after: string;
  changed: boolean;
}

function buildExtractionRows(
  beforeDoc: Record<string, any>,
  afterDoc: Record<string, any>
): ExtractionRow[] {
  const allKeys = new Set([
    ...Object.keys(beforeDoc),
    ...Object.keys(afterDoc),
  ]);
  const rows: ExtractionRow[] = [];
  for (const field of allKeys) {
    if (field === 'lineItems') continue;
    const b = beforeDoc[field] != null ? String(beforeDoc[field]) : '';
    const a = afterDoc[field] != null ? String(afterDoc[field]) : '';
    rows.push({ field, before: b, after: a, changed: b !== a });
  }
  return rows.sort((a, b) => (b.changed ? 1 : 0) - (a.changed ? 1 : 0));
}

// ── List view ─────────────────────────────────────────────────────────────────

export function Lab() {
  const [records, setRecords] = useState<LabRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextKey, setNextKey] = useState<string | null>(null);
  const [selected, setSelected] = useState<LabRecord | null>(null);
  const [nameFilter, setNameFilter] = useState('');

  useEffect(() => {
    setLoading(true);
    mappingApi
      .labListRecords()
      .then((res) => {
        setRecords(res.data.records);
        setNextKey(res.data.nextKey);
      })
      .catch(() => setRecords([]))
      .finally(() => setLoading(false));
  }, []);

  const loadMore = () => {
    if (!nextKey) return;
    setLoadingMore(true);
    mappingApi
      .labListRecords(nextKey)
      .then((res) => {
        setRecords((prev) => [...prev, ...res.data.records]);
        setNextKey(res.data.nextKey);
      })
      .catch((e) => {
        console.log(e);
      })
      .finally(() => setLoadingMore(false));
  };

  if (selected) {
    return <LabDetail record={selected} onBack={() => setSelected(null)} />;
  }

  const columns = [
    {
      title: 'Template',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, r: LabRecord) => (
        <span>
          <Text strong>{name || r.mappingKey}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {r.mappingKey}
          </Text>
        </span>
      ),
    },
    {
      title: 'Decision',
      dataIndex: 'decision',
      key: 'decision',
      render: (d: string | null) => (
        <Tag color={decisionColor(d)}>{d ?? '—'}</Tag>
      ),
    },
    {
      title: 'Similarity',
      dataIndex: 'structuralSimilarity',
      key: 'structuralSimilarity',
      render: (v: number | null) => pct(v),
    },
    {
      title: 'Score before → after',
      key: 'scores',
      render: (_: any, r: LabRecord) => (
        <Space>
          <Text type="secondary">{pct(r.originalScore?.overall)}</Text>
          <ArrowRightOutlined style={{ fontSize: 10, color: '#aaa' }} />
          <Text>{pct(r.refinedScore?.overall)}</Text>
        </Space>
      ),
    },
    {
      title: 'Changes',
      key: 'changes',
      render: (_: any, r: LabRecord) => {
        const count = r.appliedPatch ? Object.keys(r.appliedPatch).length : 0;
        return count > 0 ? (
          <Tag>
            {count} field{count > 1 ? 's' : ''}
          </Tag>
        ) : (
          <Text type="secondary">—</Text>
        );
      },
    },
    {
      title: '',
      key: 'action',
      render: (_: any, r: LabRecord) => (
        <Button size="small" onClick={() => setSelected(r)}>
          Inspect
        </Button>
      ),
    },
  ];

  const filtered = nameFilter.trim()
    ? records.filter((r) => {
        const q = nameFilter.trim().toLowerCase();
        return (
          r.name?.toLowerCase().includes(q) ||
          r.mappingKey.toLowerCase().includes(q)
        );
      })
    : records;

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <ExperimentOutlined style={{ fontSize: 20 }} />
        <Title level={3} style={{ margin: 0 }}>
          Lab
        </Title>
      </Space>
      <Space
        style={{
          marginBottom: 16,
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Text type="secondary">
          Re-adjusted templates — compare extraction before and after the patch.
        </Text>
        <Input
          placeholder="Filter by name"
          prefix={<SearchOutlined />}
          allowClear
          style={{ width: 240 }}
          value={nameFilter}
          onChange={(e) => setNameFilter(e.target.value)}
        />
      </Space>
      <Table
        dataSource={filtered}
        columns={columns}
        rowKey="hash"
        loading={loading}
        pagination={{ pageSize: 20 }}
        locale={{
          emptyText: <Empty description="No re-adjusted records found" />,
        }}
        footer={
          nextKey
            ? () => (
                <div style={{ textAlign: 'center' }}>
                  <Button onClick={loadMore} loading={loadingMore}>
                    Load more
                  </Button>
                </div>
              )
            : undefined
        }
      />
    </div>
  );
}

// ── Detail view ───────────────────────────────────────────────────────────────

function LabDetail({
  record,
  onBack,
}: {
  record: LabRecord;
  onBack: () => void;
}) {
  const [detail, setDetail] = useState<LabRecordDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(true);
  const [side, setSide] = useState<Side>('after');
  const [hoveredField, setHoveredField] = useState<string | null>(null);
  const [geometry, setGeometry] = useState<PdfPageGeometry | null>(null);
  const [patternBoxes, setPatternBoxes] = useState<{
    before: PatternBoxMap;
    after: PatternBoxMap;
  }>({ before: {}, after: {} });

  useEffect(() => {
    mappingApi
      .labGetRecord(record.hash)
      .then((res) => setDetail(res.data))
      .catch(() => setDetail(null))
      .finally(() => setLoadingDetail(false));
  }, [record.hash]);

  // Resolve pattern boxes client-side once detail is loaded
  useEffect(() => {
    if (
      !detail?.pdfUrl ||
      !detail.pdfWidth ||
      !detail.pdfHeight ||
      !record.appliedPatch
    )
      return;
    const changedPatternKeys = Object.keys(record.appliedPatch).filter((k) =>
      k.endsWith('Pattern')
    );
    if (!changedPatternKeys.length) return;

    console.log('changedPatternKeys', changedPatternKeys);
    console.log('patternBoxes', patternBoxes);

    resolvePatternBoxes(
      detail.pdfUrl,
      changedPatternKeys,
      detail.beforeConfig,
      detail.afterConfig,
      detail.pdfWidth,
      detail.pdfHeight
    ).then(setPatternBoxes);
  }, [detail, record.appliedPatch]);

  const beforeDoc = detail
    ? extractDocData(detail.originalExtraction, record.mappingKey)
    : {};
  const afterDoc = detail
    ? extractDocData(detail.refinedExtraction, record.mappingKey)
    : {};

  const rows = detail ? buildExtractionRows(beforeDoc, afterDoc) : [];
  const patchEntries = record.appliedPatch
    ? Object.entries(record.appliedPatch)
    : [];

  // Changed coord boxes (from config diff) + pattern boxes (client-side resolved)
  const coordBoxes =
    geometry && detail
      ? buildChangedCoordBoxes(
          record.appliedPatch,
          detail.beforeConfig,
          detail.afterConfig,
          geometry
        )
      : { before: [], after: [] };

  const changedBoxes = {
    before: [...coordBoxes.before, ...patternMapToBoxes(patternBoxes.before)],
    after: [...coordBoxes.after, ...patternMapToBoxes(patternBoxes.after)],
  };

  // Active boxes depend on which side we're showing
  const activeBoxes =
    side === 'before' ? changedBoxes.before : changedBoxes.after;

  const captureGeometry = useCallback((geo: PdfPageGeometry) => {
    setGeometry((prev) => {
      if (
        !prev ||
        prev.pageWidth !== geo.pageWidth ||
        prev.pageHeight !== geo.pageHeight
      ) {
        return geo;
      }
      return prev;
    });
  }, []);

  return (
    <div>
      {/* Header */}
      <Space style={{ marginBottom: 16 }} wrap>
        <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
          Back
        </Button>
        <Title level={4} style={{ margin: 0 }}>
          {record.name || record.mappingKey}
        </Title>
        <Tag color={decisionColor(record.decision)}>
          {record.decision ?? '—'}
        </Tag>
        <Text type="secondary" copyable style={{ fontSize: 12 }}>
          {record.mappingKey}
        </Text>
      </Space>

      {loadingDetail ? (
        <div style={{ textAlign: 'center', padding: 64 }}>
          <Spin indicator={<LoadingOutlined spin />} />
        </div>
      ) : (
        <Row gutter={16} style={{ height: 'calc(100vh - 180px)' }}>
          {/* Left: PDF viewer */}
          <Col span={12} style={{ height: '100%', overflow: 'hidden' }}>
            <Card
              size="small"
              style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
              bodyStyle={{ flex: 1, overflow: 'hidden', padding: 0 }}
              title={<Text>Document</Text>}
            >
              {detail?.pdfUrl ? (
                <PdfViewer
                  url={detail.pdfUrl}
                  overlay={(geo: PdfPageGeometry) => {
                    captureGeometry(geo);
                    return (
                      <>
                        {activeBoxes
                          .filter((b) => b.page === geo.pageNumber)
                          .map((b) => {
                            const px = normalizedToPixels(
                              b.normalized,
                              geo.pageWidth,
                              geo.pageHeight,
                              geo.scale
                            );
                            const isHovered = hoveredField === b.fieldName;
                            const color =
                              side === 'after' ? '#3f8600' : '#cf1322';
                            return (
                              <div
                                key={b.coordKey}
                                style={{
                                  position: 'absolute',
                                  left: px.left,
                                  top: px.top,
                                  width: px.width,
                                  height: px.height,
                                  border: `${
                                    isHovered ? 2.5 : 1.5
                                  }px solid ${color}`,
                                  background: isHovered
                                    ? `${color}22`
                                    : `${color}11`,
                                  pointerEvents: 'none',
                                  boxSizing: 'border-box',
                                }}
                              >
                                <span
                                  style={{
                                    position: 'absolute',
                                    top: -16,
                                    left: 0,
                                    fontSize: 9,
                                    background: color,
                                    color: '#fff',
                                    padding: '0 3px',
                                    borderRadius: 2,
                                    whiteSpace: 'nowrap',
                                    pointerEvents: 'none',
                                  }}
                                >
                                  {b.label}
                                </span>
                              </div>
                            );
                          })}
                      </>
                    );
                  }}
                />
              ) : (
                <Empty
                  description="PDF not available"
                  style={{ marginTop: 40 }}
                />
              )}
            </Card>
          </Col>

          {/* Right: changes + extraction values */}
          <Col span={12} style={{ height: '100%', overflowY: 'auto' }}>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              {/* Score summary */}
              <Card size="small">
                <Space size="large">
                  <Statistic
                    title="Score before"
                    value={pct(record.originalScore?.overall)}
                    valueStyle={{ color: '#cf1322', fontSize: 16 }}
                  />
                  <ArrowRightOutlined
                    style={{ marginTop: 20, color: '#aaa' }}
                  />
                  <Statistic
                    title="Score after"
                    value={pct(record.refinedScore?.overall)}
                    valueStyle={{ color: '#3f8600', fontSize: 16 }}
                  />
                  <Statistic
                    title="Similarity"
                    value={pct(record.structuralSimilarity)}
                    valueStyle={{ fontSize: 16 }}
                  />
                </Space>
              </Card>

              {/* Score issues */}
              {(() => {
                const beforeIssues = record.originalScore?.issues ?? [];
                const afterIssues = record.refinedScore?.issues ?? [];
                if (!beforeIssues.length && !afterIssues.length) return null;

                // Issues resolved = in before but not after; new = in after but not before
                const resolved = beforeIssues.filter(
                  (i) => !afterIssues.includes(i)
                );
                const added = afterIssues.filter(
                  (i) => !beforeIssues.includes(i)
                );
                const unchanged = afterIssues.filter((i) =>
                  beforeIssues.includes(i)
                );

                type IssueRow = {
                  key: string;
                  issue: string;
                  status: 'resolved' | 'added' | 'unchanged';
                };
                const rows: IssueRow[] = [
                  ...resolved.map((issue) => ({
                    key: `r-${issue}`,
                    issue,
                    status: 'resolved' as const,
                  })),
                  ...added.map((issue) => ({
                    key: `a-${issue}`,
                    issue,
                    status: 'added' as const,
                  })),
                  ...unchanged.map((issue) => ({
                    key: `u-${issue}`,
                    issue,
                    status: 'unchanged' as const,
                  })),
                ];

                return (
                  <Card size="small" title="Score issues">
                    <Table
                      size="small"
                      dataSource={rows}
                      pagination={false}
                      showHeader={false}
                      columns={[
                        {
                          key: 'status',
                          width: 20,
                          render: (_: any, row: IssueRow) => {
                            if (row.status === 'resolved')
                              return (
                                <span
                                  style={{ color: '#3f8600', fontWeight: 700 }}
                                >
                                  ✓
                                </span>
                              );
                            if (row.status === 'added')
                              return (
                                <span
                                  style={{ color: '#cf1322', fontWeight: 700 }}
                                >
                                  ✗
                                </span>
                              );
                            return <span style={{ color: '#aaa' }}>–</span>;
                          },
                        },
                        {
                          dataIndex: 'issue',
                          key: 'issue',
                          render: (v: string, row: IssueRow) => (
                            <Text
                              style={{
                                fontSize: 12,
                                color:
                                  row.status === 'resolved'
                                    ? '#3f8600'
                                    : row.status === 'added'
                                    ? '#cf1322'
                                    : '#888',
                                textDecoration:
                                  row.status === 'resolved'
                                    ? 'line-through'
                                    : undefined,
                              }}
                            >
                              {v}
                            </Text>
                          ),
                        },
                      ]}
                    />
                  </Card>
                );
              })()}

              {/* Config patch */}
              {patchEntries.length > 0 && (
                <Card size="small" title="Config changes">
                  <Table
                    size="small"
                    dataSource={patchEntries.map(([field, { from, to }]) => ({
                      field,
                      from,
                      to,
                      key: field,
                    }))}
                    pagination={false}
                    columns={[
                      {
                        title: 'Field',
                        dataIndex: 'field',
                        key: 'field',
                        width: 200,
                      },
                      {
                        title: 'Before',
                        dataIndex: 'from',
                        key: 'from',
                        render: (v: unknown) => (
                          <Text code style={{ color: '#cf1322', fontSize: 11 }}>
                            {v === null || v === undefined
                              ? '(none)'
                              : String(v)}
                          </Text>
                        ),
                      },
                      {
                        title: 'After',
                        dataIndex: 'to',
                        key: 'to',
                        render: (v: unknown) => (
                          <Text code style={{ color: '#3f8600', fontSize: 11 }}>
                            {v === null || v === undefined
                              ? '(none)'
                              : String(v)}
                          </Text>
                        ),
                      },
                    ]}
                  />
                </Card>
              )}

              {/* Extraction values */}
              <Card
                size="small"
                title="Extracted values"
                extra={
                  <Segmented
                    size="small"
                    options={[
                      { label: 'Before', value: 'before' },
                      { label: 'After', value: 'after' },
                    ]}
                    value={side}
                    onChange={(v) => setSide(v as Side)}
                  />
                }
              >
                {rows.length === 0 ? (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No extraction data — open this record from the list to load"
                  />
                ) : (
                  <Table
                    size="small"
                    dataSource={rows.map((r) => ({ ...r, key: r.field }))}
                    pagination={false}
                    onRow={(row) => ({
                      onMouseEnter: () => setHoveredField(row.field),
                      onMouseLeave: () => setHoveredField(null),
                      style: {
                        cursor: activeBoxes.some(
                          (b) => b.fieldName === row.field
                        )
                          ? 'pointer'
                          : 'default',
                        background:
                          hoveredField === row.field ? '#fafafa' : undefined,
                      },
                    })}
                    columns={[
                      {
                        title: 'Field',
                        dataIndex: 'field',
                        key: 'field',
                        width: 180,
                        render: (f: string, row: ExtractionRow) => (
                          <Space size={4}>
                            {row.changed && (
                              <span style={{ color: '#1890ff', fontSize: 10 }}>
                                ●
                              </span>
                            )}
                            <Text style={{ fontSize: 12 }}>{f}</Text>
                          </Space>
                        ),
                      },
                      {
                        title: side === 'before' ? 'Before' : 'After',
                        key: 'value',
                        render: (_: any, row: ExtractionRow) => {
                          const val =
                            side === 'before' ? row.before : row.after;
                          const other =
                            side === 'before' ? row.after : row.before;
                          return (
                            <Tooltip
                              title={
                                row.changed
                                  ? `${
                                      side === 'before' ? 'After' : 'Before'
                                    }: ${other || '(empty)'}`
                                  : undefined
                              }
                            >
                              <Text
                                style={{
                                  fontSize: 12,
                                  color: row.changed
                                    ? side === 'after'
                                      ? '#3f8600'
                                      : '#cf1322'
                                    : undefined,
                                  fontWeight: row.changed ? 500 : undefined,
                                }}
                              >
                                {val || <Text type="secondary">(empty)</Text>}
                              </Text>
                            </Tooltip>
                          );
                        },
                      },
                    ]}
                  />
                )}

                {/* Line items if present */}
                {(() => {
                  const liDoc = side === 'before' ? beforeDoc : afterDoc;
                  const rawItems: any[] = Array.isArray(liDoc.lineItems)
                    ? liDoc.lineItems
                    : [];
                  if (rawItems.length === 0) return null;

                  const colDefs: { fieldName: string; displayName: string }[] =
                    detail?.lineItemColumns ??
                    (Array.isArray(rawItems[0])
                      ? rawItems[0].map((_: any, i: number) => ({
                          fieldName: String(i),
                          displayName: String(i),
                        }))
                      : Object.keys(rawItems[0] ?? {}).map((k) => ({
                          fieldName: k,
                          displayName: k,
                        })));

                  // Convert array-of-arrays → array-of-objects using colDefs field names
                  const lineItems = rawItems.map((row, i) => {
                    if (Array.isArray(row)) {
                      const obj: Record<string, any> = { _key: i };
                      colDefs.forEach(({ fieldName }, idx) => {
                        obj[fieldName] = row[idx] ?? '';
                      });
                      return obj;
                    }
                    return { ...row, _key: i };
                  });

                  return (
                    <>
                      <Divider style={{ margin: '8px 0' }}>Line items</Divider>
                      <Table
                        size="small"
                        dataSource={lineItems}
                        rowKey="_key"
                        pagination={false}
                        columns={colDefs.map(({ fieldName, displayName }) => ({
                          title: displayName,
                          dataIndex: fieldName,
                          key: fieldName,
                          render: (v: any) => (
                            <Text style={{ fontSize: 11 }}>
                              {v != null ? String(v) : ''}
                            </Text>
                          ),
                        }))}
                        scroll={{ x: true }}
                      />
                    </>
                  );
                })()}
              </Card>
            </Space>
          </Col>
        </Row>
      )}
    </div>
  );
}

export default Lab;
